import { computed, Injectable, signal } from '@angular/core';
import type { AuthAccountRole, AuthTokens } from '../../../features/staff/auth/models/staff-auth.models';
const ACCESS_TOKEN_KEY = 'live_postblank_access_token';
const REFRESH_TOKEN_KEY = 'live_postblank_refresh_token';
const ACCOUNT_ROLE_KEY = 'live_postblank_account_role';
const ACTIVE_BROWSER_SESSION_KEY = 'live_postblank_active_browser_session';
@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly accessTokenState = signal<string | null>(this.readLocal(ACCESS_TOKEN_KEY));
  private readonly refreshTokenState = signal<string | null>(this.readLocal(REFRESH_TOKEN_KEY));
  private readonly accountRoleState = signal<AuthAccountRole | null>(this.readAccountRole());
  private readonly activeBrowserSessionState = signal(this.readSession(ACTIVE_BROWSER_SESSION_KEY) === 'true');
  readonly accessToken = this.accessTokenState.asReadonly();
  readonly refreshToken = this.refreshTokenState.asReadonly();
  readonly accountRole = this.accountRoleState.asReadonly();
  readonly activeBrowserSession = this.activeBrowserSessionState.asReadonly();
  readonly isAuthenticated = computed(() => Boolean(this.accessTokenState() || this.refreshTokenState()));
  setTokens(tokens: AuthTokens, role?: AuthAccountRole): void {
    this.accessTokenState.set(tokens.access_token);
    this.refreshTokenState.set(tokens.refresh_token);
    this.writeLocal(ACCESS_TOKEN_KEY, tokens.access_token);
    this.writeLocal(REFRESH_TOKEN_KEY, tokens.refresh_token);
    if (role)
      this.setAccountRole(role);
    this.markBrowserSessionActive();
  }
  getAccessToken(): string | null { return this.accessTokenState(); }
  getRefreshToken(): string | null { return this.refreshTokenState(); }
  getAccountRole(): AuthAccountRole | null { return this.accountRoleState(); }
  hasTokens(): boolean { return Boolean(this.getAccessToken() || this.getRefreshToken()); }
  hasRole(role: AuthAccountRole): boolean { return this.accountRoleState() === role; }
  setAccountRole(role: AuthAccountRole): void {
    this.accountRoleState.set(role);
    this.writeLocal(ACCOUNT_ROLE_KEY, role);
  }
  hasActiveBrowserSession(): boolean { return this.activeBrowserSessionState(); }
  markBrowserSessionActive(): void {
    this.activeBrowserSessionState.set(true);
    this.writeSession(ACTIVE_BROWSER_SESSION_KEY, 'true');
  }
  getRoleHomeUrl(role: AuthAccountRole | null = this.getAccountRole()): string | null {
    switch (role) {
      case 'admin': return '/staff/admin/analytics';
      case 'manager': return '/staff/manager/analytics';
      default: return null;
    }
  }
  clearTokens(): void {
    this.accessTokenState.set(null);
    this.refreshTokenState.set(null);
    this.accountRoleState.set(null);
    this.activeBrowserSessionState.set(false);
    this.removeLocal(ACCESS_TOKEN_KEY);
    this.removeLocal(REFRESH_TOKEN_KEY);
    this.removeLocal(ACCOUNT_ROLE_KEY);
    this.removeSession(ACTIVE_BROWSER_SESSION_KEY);
  }
  private readAccountRole(): AuthAccountRole | null {
    const value = this.readLocal(ACCOUNT_ROLE_KEY);
    return value === 'admin' || value === 'manager' ? value : null;
  }
  private readLocal(key: string): string | null {
    if (typeof window === 'undefined')
      return null;
    try {
      return localStorage.getItem(key);
    }
    catch {
      return null;
    }
  }
  private writeLocal(key: string, value: string): void {
    if (typeof window === 'undefined')
      return;
    try {
      localStorage.setItem(key, value);
    }
    catch { /* storage can be disabled */ }
  }
  private removeLocal(key: string): void {
    if (typeof window === 'undefined')
      return;
    try {
      localStorage.removeItem(key);
    }
    catch { /* storage can be disabled */ }
  }
  private readSession(key: string): string | null {
    if (typeof window === 'undefined')
      return null;
    try {
      return sessionStorage.getItem(key);
    }
    catch {
      return null;
    }
  }
  private writeSession(key: string, value: string): void {
    if (typeof window === 'undefined')
      return;
    try {
      sessionStorage.setItem(key, value);
    }
    catch { /* storage can be disabled */ }
  }
  private removeSession(key: string): void {
    if (typeof window === 'undefined')
      return;
    try {
      sessionStorage.removeItem(key);
    }
    catch { /* storage can be disabled */ }
  }
}
